/**
 * Birth-date normalisation shared by the birth-chart and compatibility forms.
 *
 * WHY THIS EXISTS: both forms store the date as **DD-MM-YYYY** — see
 * `formData.birthDate`, written as `${dd}-${mm}-${y}` to match what the user
 * types into the field. The timezone resolver, like the calculation routes,
 * expects ISO `YYYY-MM-DD`.
 *
 * Passing the raw form value straight through made the resolver read the FIRST
 * component as the year, so `09-05-1986` became year 9, tripped the resolver's
 * 1900 floor, and every ordinary birth reported "This date is too early for
 * reliable timezone records."
 *
 * This mirrors the canonicalisation `handleSubmit` already performs, so the
 * offset displayed in the form is derived from exactly the date that will be
 * submitted — the two can no longer disagree.
 */

/**
 * Convert a form birth date to ISO `YYYY-MM-DD`.
 *
 * Accepts DD-MM-YYYY (what the forms store) and YYYY-MM-DD (already canonical),
 * including a leading `-` for BCE years. Returns `''` for anything incomplete or
 * out of range, which callers treat as "not ready yet" rather than an error —
 * important because this runs on every keystroke while the user is still typing.
 */
/**
 * Which calendar was in force? Julian before 1582-10-15, Gregorian after.
 *
 * Deliberately NOT using JavaScript `Date`: it maps years 0-99 into the 1900s
 * (`new Date(50, 1, 1)` is 1950), it cannot represent the BCE years this product
 * supports (down to -13200), and it has no Julian mode at all. Explicit
 * arithmetic is the only correct option across that range.
 */
function usesJulianCalendar(year: number, month: number, day: number): boolean {
  // Mirror of `jhora.utils.julian_day_number`: PyHora — and therefore every chart
  // this product has ever produced — uses the JULIAN calendar before 1582-10-15.
  // 1500-02-29 and -100-02-29 are real Julian leap days the engine accepts;
  // a Gregorian-only rule wrongly rejects them. Per-country adoption (Britain
  // 1752, Russia 1918) is deliberately NOT modelled: the engine has one
  // convention and validation mirrors it exactly.
  return (
    year < 1582 ||
    (year === 1582 && month < 10) ||
    (year === 1582 && month === 10 && day < 15)
  )
}

function isLeapYear(year: number, julian: boolean): boolean {
  if (year % 4 !== 0) return false
  if (julian) return true // Julian: every 4th year, no century exception
  return year % 100 !== 0 || year % 400 === 0
}

function daysInMonth(year: number, month: number, day: number): number {
  if (month === 2) return isLeapYear(year, usesJulianCalendar(year, month, day)) ? 29 : 28
  if (month === 4 || month === 6 || month === 9 || month === 11) return 30
  return 31
}

/**
 * Does this calendar date actually exist?
 *
 * Range checks alone (`1-12`, `1-31`) let 31 February and 29 February 2025
 * through. That matters more than a bad error message: Swiss Ephemeris works in
 * continuous Julian days, so it accepted `1986-02-31` and silently produced a
 * complete chart for `1986-03-03`. Verified — the two charts were identical.
 * The backend now rejects these too; this is the inline half so the user is told
 * "check the date" rather than shown a timezone failure.
 */
export function isRealCalendarDate(year: number, month: number, day: number): boolean {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return false
  if (month < 1 || month > 12) return false
  return day >= 1 && day <= daysInMonth(year, month, day)
}

export function toIsoBirthDate(raw: string): string {
  const value = String(raw || '').trim()
  if (!value) return ''

  const dmy = /^(\d{1,2})-(\d{1,2})-(-?\d{1,6})$/.exec(value)
  const ymd = /^(-?\d{1,6})-(\d{1,2})-(\d{1,2})$/.exec(value)

  let y: number
  let m: number
  let d: number
  if (dmy) {
    d = parseInt(dmy[1], 10)
    m = parseInt(dmy[2], 10)
    y = parseInt(dmy[3], 10)
  } else if (ymd) {
    y = parseInt(ymd[1], 10)
    m = parseInt(ymd[2], 10)
    d = parseInt(ymd[3], 10)
  } else {
    return ''
  }

  // Must be a date that exists, not merely one whose parts are in range.
  if (!isRealCalendarDate(y, m, d)) return ''

  const sign = y < 0 ? '-' : ''
  return `${sign}${Math.abs(y)}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}
